import { OverlayContext } from "@/components/modal/overlay-context";
import { ToastCard } from "@/components/toast/ToastCard";
import { toastOptions } from "@/components/toast/toast-options";
import { useCallback, useContext, useEffect, useLayoutEffect, useRef } from "react";
import toast, { useToaster } from "react-hot-toast";

export const ToastViewport: React.FC = () => {
  const { restoreToastFocus, dismissToastsRef } = useContext(OverlayContext)!;
  const { toasts, handlers } = useToaster(toastOptions);
  const toastsRef = useRef(toasts);
  useLayoutEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);
  useLayoutEffect(() => {
    dismissToastsRef.current = () => {
      if (!toastsRef.current.some((notification) => notification.visible)) return false;
      toast.dismiss();
      return true;
    };
    return () => {
      dismissToastsRef.current = null;
    };
  }, [dismissToastsRef]);
  const focusedId = useRef<string | null>(null);
  const hoveredId = useRef<string | null>(null);
  const paused = useRef(false);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const updateHeight = useCallback((id: string, height: number) => {
    handlersRef.current.updateHeight(id, height);
  }, []);
  const syncPause = () => {
    const shouldPause = focusedId.current != null || hoveredId.current != null;
    if (shouldPause === paused.current) return;
    paused.current = shouldPause;
    if (shouldPause) handlersRef.current.startPause();
    else handlersRef.current.endPause();
  };
  useLayoutEffect(() => {
    const visible = new Set(toasts.filter((item) => item.visible).map((item) => item.id));
    if (focusedId.current && !visible.has(focusedId.current)) {
      focusedId.current = null;
      restoreToastFocus();
    }
    if (hoveredId.current && !visible.has(hoveredId.current)) hoveredId.current = null;
    syncPause();
  });

  useEffect(
    () => () => {
      if (paused.current) handlersRef.current.endPause();
      paused.current = false;
      focusedId.current = null;
      hoveredId.current = null;
    },
    [],
  );

  return (
    <div style={{ position: "fixed", inset: 16, pointerEvents: "none" }}>
      {toasts.map((notification) => {
        const position = notification.position ?? "bottom-left";
        return (
          <ToastCard
            key={notification.id}
            notification={notification}
            position={position}
            offset={handlers.calculateOffset(notification, { gutter: 8 })}
            updateHeight={updateHeight}
            onFocus={() => {
              focusedId.current = notification.id;
              syncPause();
            }}
            onBlur={(event) => {
              if (event.currentTarget.contains(event.relatedTarget)) return;
              if (event.relatedTarget == null) {
                const card = event.currentTarget;
                // Let removal restore focus before handling an ordinary blur to non-focusable content.
                queueMicrotask(() => {
                  if (
                    focusedId.current !== notification.id ||
                    card.contains(document.activeElement)
                  )
                    return;
                  focusedId.current = null;
                  syncPause();
                });
                return;
              }
              focusedId.current = null;
              syncPause();
            }}
            onHover={(hovered) => {
              if (hovered) hoveredId.current = notification.id;
              else if (hoveredId.current === notification.id) hoveredId.current = null;
              syncPause();
            }}
          />
        );
      })}
    </div>
  );
};
